/**
 * PullRequestPlanView — the Usage page for a workspace on the pull-request
 * plan (`billingContract === "pr_plan"`): a monthly bucket of merged pull
 * requests YAGNI authored. The headline and pace, the tier ladder with what
 * counts, the month by Team, the merged list, and the guardrails.
 *
 * Counts come from `GET /api/yagni-code-usage/pull-requests?month=`; the
 * month chips drive `month`. The plan's name and bucket come from the
 * placeholder ladder (pullRequestPlan.ts), Growth until the workspace carries
 * its own tier.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { PullRequestPlanResponse } from "@yagni/shared";

import { Btn, Chip, ListHead } from "../queue/index.js";
import { useAuthSession } from "../hooks/useWorkspaceRole.js";
import { DailyCeilingField } from "./CreditsSection.js";
import { useBilling, useGuardrails } from "./billingData.js";
import {
  PLAN_TIERS,
  formatDuration,
  formatRounds,
  mergedWhen,
  monthKey,
  monthKeyBefore,
  monthName,
  nextTier,
  perPullRequestUsd,
  planPace,
  planTier,
  pullRequestCsv,
  renewsLabel,
  usePullRequestPlan,
} from "./pullRequestPlan.js";
import { Key, Legend, Note, OfTotal, Quiet, Sec, Seg, Tile, Tiles } from "./usageParts.js";
import { usdWhole } from "./usageReport.js";

/** How many merged pull requests the list shows before "and N more". */
const MERGED_ROWS = 25;

export function PullRequestPlanView({ previewSwitch, now = new Date() }: { previewSwitch?: ReactNode; now?: Date }) {
  const current = monthKey(now);
  const months = useMemo(() => [0, 1, 2].map((offset) => monthKeyBefore(now, offset)), [now]);
  const [month, setMonth] = useState(current);
  const { plan, error, loading } = usePullRequestPlan(month);
  const session = useAuthSession();
  const tier = planTier();
  const isCurrent = month === current;

  const chips = (
    <>
      {months.map((key, index) => (
        <Chip key={key} tone={month === key ? "on" : undefined} onClick={() => setMonth(key)}>
          {index === 0 ? "This month" : monthName(key)}
        </Chip>
      ))}
    </>
  );

  return (
    <>
      <ListHead
        title={`Usage · ${monthName(month)}`}
        sub={[
          `${tier.name} plan · ${tier.bucket} merged pull requests a month · renews ${renewsLabel(current)}`,
          `Past the bucket: ${usdWhole(perPullRequestUsd(tier))} each`,
        ]}
        acts={
          <>
            {previewSwitch}
            <Btn variant="secondary" size="sm" disabled={!plan || plan.rows.length === 0} onClick={() => plan && downloadCsv(plan)}>
              Download CSV
            </Btn>
          </>
        }
        chips={chips}
        picked={`${plan?.timezone ?? "Workspace time"} · as of ${now.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
      />
      {loading && <Note>Reading the month's pull requests</Note>}
      {error && <Note tone="honest">{error}</Note>}
      {plan && (
        <PlanBody
          plan={plan}
          monthKey={month}
          isCurrent={isCurrent}
          now={now}
          workspaceName={session?.workspace.name ?? null}
        />
      )}
    </>
  );
}

function downloadCsv(plan: PullRequestPlanResponse) {
  const blob = new Blob([pullRequestCsv(plan)], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `yagni-pull-requests-${plan.month}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function PlanBody({
  plan,
  monthKey: key,
  isCurrent,
  now,
  workspaceName,
}: {
  plan: PullRequestPlanResponse;
  monthKey: string;
  isCurrent: boolean;
  now: Date;
  workspaceName: string | null;
}) {
  const tier = planTier();
  const next = nextTier(tier);
  const each = perPullRequestUsd(tier);
  const pace = planPace(plan.merged, key, now);
  const month = monthName(key);
  const renews = renewsLabel(key);
  const filled = Math.min(100, Math.round((100 * plan.merged) / tier.bucket));
  const paceAt = Math.min(99.5, Math.round((100 * pace) / tier.bucket));
  const median = formatDuration(plan.medianOpenToMergedMs);
  const who = workspaceName ?? "the workspace";

  const headline = isCurrent ? (
    <>
      <b>
        {plan.merged} of {tier.bucket}
      </b>{" "}
      pull requests YAGNI authored have merged in {month} so far. At this pace {who} lands <b>{pace}</b> by {renews},{" "}
      {pace <= tier.bucket
        ? `inside the ${tier.name} plan`
        : `${pace - tier.bucket} past the ${tier.name} bucket at ${usdWhole(each)} each`}
      .{plan.medianOpenToMergedMs !== null && (
        <>
          {" "}
          Median open to merged: <b>{median}</b>.
        </>
      )}
    </>
  ) : (
    <>
      <b>
        {plan.merged} of {tier.bucket}
      </b>{" "}
      pull requests YAGNI authored merged in {month}
      {plan.merged > tier.bucket ? `, ${plan.merged - tier.bucket} past the bucket` : ""}.
      {plan.medianOpenToMergedMs !== null && (
        <>
          {" "}
          Median open to merged: <b>{median}</b>.
        </>
      )}
    </>
  );

  const shown = plan.rows.slice(0, MERGED_ROWS);
  const more = plan.merged - shown.length;

  return (
    <>
      <p className="brief" data-testid="plan-headline">
        {headline}
      </p>

      <Tiles testId="plan-tiles">
        <Tile
          value={<OfTotal n={plan.merged} of={tier.bucket} />}
          label={isCurrent ? "Merged this month" : `Merged in ${month}`}
          tone="blue"
          sub={
            plan.merged >= tier.bucket
              ? `${plan.merged - tier.bucket} past the bucket · resets ${renews}`
              : `${tier.bucket - plan.merged} left · resets ${renews}`
          }
        />
        <Tile
          value={usdWhole(tier.priceUsd)}
          label={`${tier.name} plan`}
          sub={`${usdWhole(each)} a pull request${next ? ` · next tier ${usdWhole(perPullRequestUsd(next))}` : ""}`}
        />
        <Tile value={median} label="Open to merged" tone="green" sub={`median · YAGNI authored · ${month}`} />
        <Tile
          value={plan.open}
          label="Still open"
          tone="quiet"
          sub={`${plan.closedUnmerged} closed unmerged · neither counts`}
        />
      </Tiles>

      <div style={{ padding: "14px 0 4px" }}>
        <div
          className="bigbar"
          role="img"
          aria-label={`${plan.merged} of ${tier.bucket} merged${isCurrent ? `, on pace for ${pace}` : ""}`}
          data-testid="plan-bigbar"
        >
          <i style={{ width: `${filled}%` }} />
          {isCurrent && <span className={paceAt > 85 ? "pace end" : "pace"} style={{ left: `${paceAt}%` }} data-l={`pace · ${pace}`} />}
        </div>
        <Legend style={{ marginTop: 8 }}>
          <Key className="k-bl">merged {plan.merged}</Key>
          {isCurrent && <Key color="var(--text-secondary)">on pace for {pace}</Key>}
          <span style={{ marginLeft: "auto" }}>
            past {tier.bucket}: <b style={{ color: "var(--text-primary)" }}>keep going at {usdWhole(each)} each</b> · see
            Guardrails
          </span>
        </Legend>
      </div>

      <Sec
        k="01"
        title="The plan"
        fact="a monthly bucket of merged pull requests YAGNI authored · cheaper each as the bucket grows"
        testId="plan-ladder"
      >
        <div className="tiers">
          {PLAN_TIERS.map((rung) => (
            <div key={rung.name} className={rung.name === tier.name ? "tier on" : "tier"} data-testid={`tier-${rung.name}`}>
              <span className="nm">{rung.name}</span>
              {rung.name === tier.name && <span className="now">current</span>}
              <span className="p">
                {usdWhole(rung.priceUsd)}
                <span style={{ font: "400 12px/1 var(--mono)", color: "var(--text-tertiary)" }}> /mo</span>
              </span>
              <span className="q">{rung.bucket} merged pull requests</span>
              <span className="each">{usdWhole(perPullRequestUsd(rung))} each</span>
            </div>
          ))}
          <div className="tier">
            <span className="nm">Enterprise</span>
            <span className="p" style={{ fontSize: 15, paddingTop: 4 }}>
              Metered
            </span>
            <span className="q">The rate card, invoiced monthly</span>
            <span className="each">premium per token · no bucket</span>
          </div>
        </div>
        <div className="dashed" style={{ padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5 }}>
          <b style={{ color: "var(--text-primary)" }}>What counts.</b> A pull request Wright opened on a connected
          repository, merged by a person. Closed unmerged, reverted inside 7 days, or authored by a person: not
          counted. Proctor's reviews and Fletcher's tests are inside the plan, on every pull request the Teams own.
          <span className="fact" style={{ display: "block", marginTop: 4 }}>
            The ladder here is a placeholder from the Oct 9 call, not a price list.
          </span>
        </div>
      </Sec>

      <Sec k="02" title="By Team" fact={month} testId="plan-by-team">
        {plan.byTeam.length === 0 ? (
          <Note>No Team opened a pull request in {month}.</Note>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>Team</th>
                  <th className="num">Merged</th>
                  <th className="num">Open</th>
                  <th className="num">Closed unmerged</th>
                  <th className="num">Open to merged</th>
                  <th className="num">Review rounds</th>
                </tr>
              </thead>
              <tbody>
                {plan.byTeam.map((team) => (
                  <tr key={team.deskId}>
                    <td>
                      <Link to={`/desks/${encodeURIComponent(team.deskId)}`}>{team.name}</Link>
                    </td>
                    <td className="num" style={{ color: "var(--accent)", fontWeight: 600 }}>
                      {team.merged}
                    </td>
                    <td className="num">{team.open}</td>
                    <td className="num">{team.closedUnmerged || <Quiet />}</td>
                    <td className="num">{formatDuration(team.medianOpenToMergedMs)}</td>
                    <td className="num">{formatRounds(team.reviewRoundsAvg)}</td>
                  </tr>
                ))}
                <tr className="total">
                  <td>{workspaceName ?? "Workspace"}</td>
                  <td className="num">{plan.merged}</td>
                  <td className="num">{plan.open}</td>
                  <td className="num">{plan.closedUnmerged}</td>
                  <td className="num">{median}</td>
                  <td className="num">{formatRounds(weightedRounds(plan))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <Note>Review rounds are fix rounds per merged pull request; 0 means it merged as first reviewed.</Note>
      </Sec>

      <Sec k="03" title={isCurrent ? "Merged this month" : `Merged in ${month}`} fact={`${plan.merged} · newest first`} testId="plan-merged">
        {shown.length === 0 ? (
          <Note>Nothing YAGNI authored has merged in {month} yet.</Note>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>Pull request</th>
                  <th>Title</th>
                  <th>Team</th>
                  <th>Repository</th>
                  <th>Merged by</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((row) => (
                  <tr key={`${row.repo}${row.pr}`}>
                    <td style={{ font: "400 12px/1.4 var(--mono)" }}>
                      <a href={row.url} target="_blank" rel="noreferrer">
                        {row.pr}
                      </a>
                    </td>
                    <td style={{ whiteSpace: "normal" }}>{row.title}</td>
                    <td>{row.teamName}</td>
                    <td className="fact">{row.repo}</td>
                    <td>{row.mergedBy ?? <Quiet />}</td>
                    <td className="fact">{mergedWhen(row.mergedAt, now)}</td>
                  </tr>
                ))}
                {more > 0 && (
                  <tr>
                    <td colSpan={6} className="fact">
                      and {more} more{plan.rows.length > shown.length ? " in the CSV" : ""}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Sec>

      <PlanGuardrails each={each} />
    </>
  );
}

/** The workspace's review rounds: each Team's average weighted by its merged count. */
function weightedRounds(plan: PullRequestPlanResponse): number | null {
  let merged = 0;
  let rounds = 0;
  for (const team of plan.byTeam) {
    if (team.reviewRoundsAvg === null || team.merged === 0) continue;
    merged += team.merged;
    rounds += team.reviewRoundsAvg * team.merged;
  }
  return merged === 0 ? null : rounds / merged;
}

function PlanGuardrails({ each }: { each: number }) {
  const guardrails = useGuardrails();
  const billing = useBilling();
  const tier = planTier();
  return (
    <Sec
      k="04"
      title="Guardrails and billing"
      testId="plan-guardrails"
      right={
        <Btn variant="secondary" size="sm" disabled={billing.busy} onClick={() => void billing.manageBilling()}>
          Manage billing
        </Btn>
      }
    >
      <div className="field" style={{ gridTemplateColumns: "160px 1fr" }}>
        <label>Past the bucket</label>
        <div className="inline">
          <Seg
            label="Past the bucket"
            value="keep"
            options={[
              { key: "keep", label: `Keep going at ${usdWhole(each)} each` },
              { key: "pause", label: "Pause and ask", disabled: true },
            ]}
          />
          <span className="fact" style={{ fontSize: 11 }}>
            Set on the contract for now. Either way the queue says so the day it happens.
          </span>
        </div>
      </div>
      <DailyCeilingField
        state={guardrails}
        labelWidth={160}
        help="Credits a day across every Team, on the metered side only. Teams stop cleanly at the ceiling and say so."
      />
      {billing.actionError && (
        <p role="alert" className="fact" style={{ color: "var(--honesty)" }}>
          {billing.actionError}
        </p>
      )}
      <div className="checks">
        <div>
          <span className="ok">Plan</span>
          <span>
            {tier.name} · {usdWhole(tier.priceUsd)} a month · renews on the 1st
          </span>
        </div>
        <div>
          <span className="ok">Invoice</span>
          <span>Issued on the 1st for the month before; invoices and receipts are under Manage billing.</span>
        </div>
      </div>
    </Sec>
  );
}
